export type ID = string;

export type QuotationKind = 'IOCT' | 'ACTI';

// Chosen once at quotation creation (alongside kind/recipient) and fixed for
// that quotation's life — same convention as `kind` itself, which also can't
// be changed after creation. Governs which sections are editable in
// CalcsheetQuotationEditor: 'supply' locks A (General Requirements) and C
// (Services/Manpower), leaving only B (Components) editable; 'services' locks
// only B, leaving A and C editable; 'both' (the default, including every
// quotation created before this field existed) locks nothing.
export type QuotationScopeCategory = 'supply' | 'services' | 'both';

export type ProjectStatus = 'draft' | 'for_review' | 'sent' | 'won' | 'lost' | 'inactive';

// Single runtime source of truth for the status set, in lifecycle order. Status
// dropdowns and the XLSX export's Excel dropdown all derive from this list —
// adding a status here propagates everywhere.
export const PROJECT_STATUSES: ProjectStatus[] = ['draft', 'for_review', 'sent', 'won', 'lost', 'inactive'];

export function projectStatusLabel(s: ProjectStatus): string {
  if (s === 'for_review') return 'For review';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Sales' subjective read on how likely an opportunity is to close, independent
// of pipeline status (a project can be 'sent' and still graded A or C). Not
// set on legacy/imported projects — the UI shows "Ungraded" until a grade is
// explicitly picked.
export type OpportunityGrade = 'A' | 'B' | 'C';

export const OPPORTUNITY_GRADES: OpportunityGrade[] = ['A', 'B', 'C'];

// Win-likelihood weight behind each grade, as a whole-number percentage.
export const OPPORTUNITY_GRADE_WEIGHT: Record<OpportunityGrade, number> = {
  A: 100,
  B: 60,
  C: 30,
};

export function opportunityGradeLabel(g: OpportunityGrade): string {
  return `${g} — ${OPPORTUNITY_GRADE_WEIGHT[g]}%`;
}

// Re-export the unified Client + ClientContact types so calcsheet code that imports
// from `types/Quotation` keeps working without a path change.
export type { Client, ClientContact, Gender } from './Client';
export { primaryContact, resolveContact } from './Client';

export interface SalesContact {
  id: ID;
  name: string;
  position?: string;
  email?: string;
  phone?: string;
  // Overrides shown on the PDF signature block when the quotation is issued
  // under ACTI (quotation.kind === 'ACTI') instead of IOCT — e.g. a different
  // title/email under the ACTI partnership. Falls back to position/email when unset.
  actiPosition?: string;
  actiEmail?: string;
}

export interface Project {
  id: ID;
  code: string;
  name: string;
  location?: string;
  date: string;
  customerId: ID | null;
  partnerId: ID | null;
  salesContactId: ID | null;
  status: ProjectStatus;
  ongoing?: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;

  // Sales' subjective win-likelihood grade for this opportunity. See
  // `OpportunityGrade` for the A/B/C meanings. Undefined = ungraded.
  opportunityGrade?: OpportunityGrade;

  // Who created the opportunity — stamped server-side from the authenticated user
  // on POST and never updatable (PUT strips them). Absent on legacy/imported and
  // pre-2026-07 projects; UI renders '—'.
  createdBy?: string;
  createdByName?: string;

  // OneDrive corporate-shared-library folder links. Populated best-effort when a
  // project is created (proposal folder) or transitions to 'won' (execution folder).
  // Absence is normal for older projects or when OneDrive is offline/unconfigured.
  proposalFolderId?: string;
  proposalFolderUrl?: string;
  executionFolderId?: string;
  executionFolderUrl?: string;

  // Link to the original Project List module after a proposal is won. The
  // main project remains the operations record; Calcsheet only stores the link
  // and sync status so accidental status flips can be recovered without
  // deleting operational data.
  mainProjectId?: string;
  mainProjectNo?: string;
  mainProjectLinkedAt?: string;
  mainProjectLastSyncedAt?: string;
  mainProjectSyncStatus?: 'none' | 'linked' | 'missing' | 'unlinked' | 'error';
  mainProjectSyncError?: string;
  mainProjectUnlinkedAt?: string;
  mainProjectUnlinkReason?: string;
  mainProjectStatus?: string;
  mainProjectProgressPercent?: number;
  mainProjectCompletionDate?: number | string | null;
  mainProjectStatusSyncedAt?: string;

  // Customer PO documents attached for easy reference. Populated when the user
  // attaches a PO — typically prompted right after marking the project 'won',
  // but also available any time from the project detail page. Files live in
  // the project's OneDrive execution folder under a "Customer PO" subfolder;
  // this array only stores pointers + light metadata, never the file bytes.
  customerPOs?: CustomerPO[];

  // Price lock frozen when the project is won (POST /calcsheet/projects/:id/award).
  // The linked Project List contract amount = `price`; its budget seeds from `budget`.
  awarded?: AwardedQuotation;
}

export interface AwardedQuotation {
  quotationId: ID;
  kind: QuotationKind | string;
  revision: string;
  /** Contract amount (Sales scope: grand total, or services-only when materials are client-supplied). */
  price: number;
  grandTotal: number;
  /** Spendable budget: VAT-ex value minus margin (ioctCostBasis). */
  budget: number;
  awardedAt: string;
  awardedBy: string;
  awardedById?: string;
}

export interface CustomerPO {
  id: ID;                 // uuid
  poNumber?: string;
  poDate?: string;         // ISO date (yyyy-mm-dd)
  fileName: string;
  driveItemId: string;    // OneDrive item id of the uploaded file
  webUrl?: string;         // OneDrive web link
  fileSize?: number;       // bytes
  uploadedBy?: string;     // username of uploader
  uploadedAt: string;       // ISO datetime
}

export type FormulaVersion = 'legacy' | 'current';
export type SalesValueScope = 'full_quotation' | 'services_only';

export interface QuotationImportMeta {
  sourceFile: string;
  importedAt: string;
  originalCode?: string;
  pdfFilename?: string;
}

export interface GeneralReqLine {
  id: ID;
  code: string;
  description: string;
  unitPrice: number;
  qty: number;
  uom: string;
  markupPct?: number;
}

export interface HistoricalPriceSource {
  observationId: string;
  quotationId: ID;
  projectId: ID;
  quotationReference: string;
  quotationDate: string;
  normalizedUnitCost: number;
  quotedSellingUnit?: number;
  selectedAt: string;
  suggestedContingencyPct?: number;
  suggestionMethod?: 'quarterly' | 'annualized';
  suggestionConfidence?: 'high' | 'medium' | 'low';
}

export interface ComponentLine {
  id: ID;
  code: string;
  description: string;
  brand?: string;
  partNo?: string;
  qty: number;
  uom: string;
  unitCost: number;
  forex: number;
  contingencyPct: number;
  contingencyPctOverridden?: boolean;
  discountPct: number;
  leadTimeDays?: number;
  group?: string;
  /** A calculation-neutral label rendered above this contiguous block of items. */
  subheader?: string;
  markupPct?: number;
  /** Per-line EWT override — same resolution pattern as markupPct (falls
   * back to Quotation.ewtPct when unset). See Quotation.ewtPct. */
  ewtPct?: number;
  expectedPurchaseDate?: string;
  historicalPriceSource?: HistoricalPriceSource;
  /** Free-text note for the team only — price source, "budgetary", pricelist
   * name, who quoted it. Never printed on the PDF or Excel (exporters use
   * explicit public fields). Put internal text here, not in `description`. */
  internalNote?: string;
  /** Optional item: priced for reference but NOT included in the contract
   * cost/subtotal/grand total. Listed in a separate "Optional Items" section
   * on exports so the client can add it if they avail. */
  optional?: boolean;
  /** Section-header row: a label-only line (uses `description`) inserted to
   * break the BOM into named sections on screen and on export. Carries no
   * qty/cost and is excluded from every totals calculation. */
  isHeader?: boolean;
  /** Child (second-level) header — nests under a preceding `isHeader` row to
   * break a section into named sub-groups (e.g. a "FIELD INSTRUMENTS" header
   * with "Temperature" / "Pressure" child headers underneath). Same label-only,
   * no qty/cost, excluded-from-totals behavior as `isHeader`, just rendered
   * indented one level. */
  isChildHeader?: boolean;
}

export interface ServiceLine {
  id: ID;
  code: string;
  description: string;
  amount: number;
  /** @deprecated superseded by `qty` (per-line pricing is now QTY x Unit
   * Price, not day-count). Kept so older quotations' stored values still
   * read correctly; still feeds the delivery-terms installation-duration
   * estimate, which is specifically calendar days. */
  days?: number;
  /** Per-line pricing: quantity of units (e.g. days, lots, pax) — see `uom`. */
  qty?: number;
  /** Per-line pricing: unit of measure for `qty` (e.g. "day", "lot", "pax"). */
  uom?: string;
  /** Per-line pricing: price per unit. Falls back to the shared team daily
   * rate (manpowerDailyRate) when unset — same override pattern as
   * markupPct falling back to Quotation.laborMarkupPct. */
  unitPrice?: number;
  group?: string;
  /** A calculation-neutral label rendered above this contiguous block of items. */
  subheader?: string;
  markupPct?: number;
  /** Team-only note — never printed on the PDF or Excel. See ComponentLine.internalNote. */
  internalNote?: string;
  /** Section-header row — same label-only, excluded-from-totals row as
   * ComponentLine.isHeader, for breaking Engineering Services into named
   * sections. */
  isHeader?: boolean;
  /** Child (second-level) header — see ComponentLine.isChildHeader. */
  isChildHeader?: boolean;
}

export interface ManpowerEntry {
  id: ID;
  role: string;
  group: 'engineering' | 'labor';
  headcount: number;
  mandays: number;
  dailyRate: number;
  allowance: number;
  presetId?: ID | null;
}

export interface LaborRolePreset {
  id: ID;
  role: string;
  group: 'engineering' | 'labor';
  dailyRate: number;
  allowance: number;
}

export interface Quotation {
  id: ID;
  projectId: ID;
  kind: QuotationKind;
  // Set when the project was awarded on this quotation (see Project.awarded).
  // Present = price-locked: the server rejects edits/deletes until an admin unlocks.
  awardedAt?: string;
  awardedBy?: string;
  awardedById?: string;
  // Absent = 'both' (every quotation created before this field existed, and
  // the default for new ones). See QuotationScopeCategory for what each value
  // locks.
  scopeCategory?: QuotationScopeCategory;
  revision: string;
  recipientId: ID | null;
  contactId?: ID;            // Which contact at the recipient client this quotation addresses
  dateSent?: string;          // YYYY-MM-DD. If blank, exports use the generation date.
  expectedPurchaseDate?: string;
  validityDays: number;
  paymentTerms: string;
  deliveryTerms: string;
  warrantyMonths: number;
  productMarkupPct: number;
  productContingencyPct?: number;
  laborMarkupPct: number;
  generalReqMarkupPct: number;
  /** Expanded Withholding Tax gross-up (IOCT quotations). Layered invisibly
   * onto the existing markup for General Requirements, Supply of Components
   * and Engineering Services — never printed as its own line/label on the
   * PDF or Excel export. New IOCT quotations default to 5%; ACTI quotations
   * and quotations created before this field existed read as 0 (unaffected)
   * unless set explicitly. */
  ewtPct?: number;
  globalContingencyPct: number;
  discountPct: number;
  vatPct: number;
  /** Issuer is a NON-VAT taxpayer (percentage tax). When true the quotation
   * never adds VAT (`vatPct` is forced to 0 in the totals), the printed PDF /
   * Excel totals read "VAT: N/A (Non-VAT)" with no "VAT-EX" / 0% VAT wording,
   * and a bold TAX STATUS clause leads the Terms and Conditions. New IOCT
   * quotations default to true; absent (older quotations) reads as false, so
   * existing printed output is unchanged until a user switches it on. */
  nonVat?: boolean;
  // ── Delivery fee & minimum-order surcharge (opt-in per quotation) ──
  // When `deliveryTermsEnabled` is true, a manual `deliveryFee` (VAT-ex freight
  // charge) is added to the quote, and orders whose goods+services subtotal
  // falls below `minOrderThreshold` (default ₱50,000) get an automatic
  // `smallOrderFee` surcharge (default ₱5,000). Delivery + surcharge are
  // VAT-able (added before VAT) and do NOT affect margin/budget (pass-through).
  // Absent/false on every existing quotation, so totals are unchanged until a
  // user opts in.
  deliveryTermsEnabled?: boolean;
  deliveryFee?: number;
  minOrderThreshold?: number;
  smallOrderFee?: number;
  generalReqts: GeneralReqLine[];
  components: ComponentLine[];
  services: ServiceLine[];
  manpower: ManpowerEntry[];
  servicesFromManpower: boolean;
  servicesPerLinePricing?: boolean;
  engineeringServicesQty?: number;
  preparedBy?: string;
  preparedByTitle?: string;       // override the auto-resolved job title in the PDF signature block
  authorizedBy?: string;
  termsOverrides?: {
    scopeOfWork?: string;         // replaces the hardcoded Scope of Work paragraph
    exclusions?: string;          // adds an Exclusions section (newline-separated bullets); omitted from the PDF entirely if blank, no default text
    basisOfProposal?: string;     // replaces the hardcoded Basis of Proposal paragraph
    deliveryLines?: string;       // replaces ALL delivery bullet lines (newline-separated, each already starts with "- ")
    warrantyExclusion?: string;   // replaces the hardcoded warranty-exclusion sentence
    taxStatus?: string;           // replaces the TAX STATUS clause printed first in the terms when `nonVat` is on
  };
  notes?: string;
  exportGeneralReqtsAsLot?: boolean;
  generalReqtsExportQty?: number;
  /** How each component group renders on the PDF/Excel, keyed by group name.
   * 'lot' (the default when a group is absent here) collapses the group to a
   * single "1.00 LOT" line priced at the group total. 'itemized' shows each
   * member's own qty + UOM while still pricing the group as one combined
   * amount. */
  componentGroupDisplay?: Record<string, 'lot' | 'itemized'>;
  /** How per-line-priced Engineering Services rows render on the PDF/Excel.
   * Unset/false (the default) collapses every row to a single "1.00 LOT" line
   * priced at the line's amount — the pre-QTY/UOM behavior. true shows each
   * row's real QTY/UOM, with the per-unit price derived from amount ÷ qty so
   * the printed unit × qty still reconciles to the line total; grouped rows
   * still collapse to one combined price on the group's middle row, same as
   * componentGroupDisplay's 'itemized' mode. */
  servicesItemizedExport?: boolean;
  /** Controls the value reported in Sales and synced to the Project List. */
  salesValueScope?: SalesValueScope;
  /** Suppresses component part numbers from the customer-facing PDF only. */
  hidePartNumbersInPdf?: boolean;
  pageBreakBeforeTerms?: boolean;
  formulaVersion?: FormulaVersion;
  generalReqContingencyMode?: 'standard' | 'baked';
  importedFrom?: QuotationImportMeta;
  legacyTotalsSnapshot?: QuotationTotals;
  createdAt: string;
  updatedAt: string;
}

// A reusable "scope bundle" saved to the shared Scope Library — a named set of
// inclusions (general requirements, components, services, manpower) plus
// optional scope-of-work / exclusions text — that can be inserted into any
// quotation. Team-shared like labor presets; stored in `calcsheet_scope_library`.
export interface ScopeBundle {
  id: ID;
  name: string;
  category?: string;          // free-text grouping, e.g. "SCADA", "Panel Build", "General Requirements"
  notes?: string;
  generalReqts: GeneralReqLine[];
  components: ComponentLine[];
  services: ServiceLine[];
  manpower: ManpowerEntry[];
  terms?: {
    scopeOfWork?: string;
    exclusions?: string;
  };
  createdBy?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

// A snapshot of a quotation's state captured server-side just before a save
// overwrote it. `data` is the full quotation as it existed before that save.
export interface QuotationVersion {
  id: ID;
  quotationId: ID;
  projectId?: ID | null;
  savedAt: string;          // when this state was replaced by a newer save
  savedBy?: string | null;  // who performed the save that replaced this state
  /** Free-text note describing what changed in the save that replaced this
   * state — typed by the user in the "What did you change?" prompt on Save.
   * Optional; absent on saves where the prompt was left blank. */
  remarks?: string | null;
  data: Quotation;
}

export interface QuotationTotals {
  generalReqtsCost: number;
  generalReqtsWithContingency: number;
  generalReqtsSubtotal: number;

  componentsCost: number;
  componentsWithContingency?: number;
  componentsSubtotal: number;
  /** Sum of optional-component selling prices — informational only, NOT part
   * of `subtotal`/`grandTotal`. Absent/0 when there are no optional items. */
  componentsOptionalSubtotal?: number;

  laborCost: number;
  laborWithContingency: number;
  servicesSubtotal: number;

  subtotal: number;
  discount: number;
  // Delivery fee & small-order surcharge (0 when the feature is off). Folded
  // into `grandTotal` before VAT; excluded from `subtotal`/margin/budget.
  // Optional so frozen legacy snapshots (which predate the feature) still type.
  deliveryFee?: number;
  smallOrderSurcharge?: number;
  deliveryTotal?: number;
  vat: number;
  grandTotal: number;
}
